import { MockServiceStore } from "./MockServiceStore";
import {
  mockStructureCastStructures,
  type StructureCastStructure,
} from "./structurecast-structures.mock";
import { mockStructureCastComponents } from "./structurecast-components.mock";
import {
  mockStructureCastEstimates,
  backfillEstimateTotals,
} from "./structurecast-estimates.mock";

/**
 * Component definition within an estimate line item (before conversion to production)
 */
export interface EstimateComponentDefinition {
  componentType: "base" | "riser" | "cone" | "flat_top";
  heightFeet: number;
  heightInches: number;
  heightFraction: string;
  quantity: number;
}

/**
 * StructureCast - Estimate Line Item (one per structure on an estimate)
 */
export interface StructureCastEstimateLineItem {
  id: string;
  estimateId: string;
  structureId: string;
  markNumber: string;
  componentCount: number;
  totalCubicYards: number;
  totalWeight: number;
  // Structure dimensions (inches)
  overallLength: number;
  overallWidth: number;
  overallHeight: number;
  stationReference: string;
  // Component definitions (pre-production)
  componentDefinitions: EstimateComponentDefinition[];
  // Cost breakdown
  concreteCost: number;
  laborCost: number;
  embedCost: number;       // Repurposed: Frame & Cover cost
  liftInsertCost: number;
  waterstopCost: number;
  subtotal: number;
  notes: string;
  createdAt: string;
  updatedAt: string;
}

/**
 * Generate line items for the Heritage Ranch WRRF estimate (Quote #25-1585, Job #25-1585).
 * Mid-State Concrete Products cost model:
 *   - Concrete: CY * $/CY rate (5,000 PSI Type II/V)
 *   - Rebar: per-piece rate * componentCount (stored in laborCost)
 *   - Ring & Cover (1900 Sanitary Sewer CPH frame + cover, HW190023): per-piece rate * componentCount (stored in embedCost)
 *   - Lift inserts, waterstop: $0 (not applicable)
 */
const generateLineItems = (): StructureCastEstimateLineItem[] => {
  const estimate = mockStructureCastEstimates.find((e) => e.id === "sc-est-1");
  if (!estimate) return [];

  const structures = mockStructureCastStructures.filter(
    (s) => s.projectId === "sc-proj-1"
  );

  let runningMaterial = 0;
  let runningLabor = 0;

  const items: StructureCastEstimateLineItem[] = structures.map(
    (struct: StructureCastStructure, idx: number) => {
      const comps = mockStructureCastComponents.filter(
        (c) => c.structureId === struct.id
      );

      const componentCount = comps.length;
      const totalCY = struct.totalCubicYards;
      const totalWeight = struct.totalWeight;

      // Concrete cost (CY * rate)
      const concreteCost = +(totalCY * estimate.rateConcretePsCy).toFixed(2);

      // Rebar cost (per-piece * count, stored in laborCost)
      const laborCost = +(componentCount * estimate.rateLaborPerPiece).toFixed(2);

      // Frame & Cover (per-piece * count, stored in embedCost)
      const embedCost = +(componentCount * estimate.rateEmbedPerPiece).toFixed(2);

      const liftInsertCost = 0;
      const waterstopCost = 0;

      const subtotal = +(
        concreteCost + laborCost + embedCost + liftInsertCost + waterstopCost
      ).toFixed(2);

      runningMaterial += concreteCost + embedCost;
      runningLabor += laborCost;

      // Build component definitions from actual components
      const componentDefinitions: EstimateComponentDefinition[] = [];
      const compsByType = new Map<string, typeof comps>();
      for (const c of comps) {
        const existing = compsByType.get(c.componentType) || [];
        existing.push(c);
        compsByType.set(c.componentType, existing);
      }
      for (const [type, typeComps] of compsByType) {
        componentDefinitions.push({
          componentType: type as EstimateComponentDefinition["componentType"],
          heightFeet: typeComps[0].heightFeet,
          heightInches: typeComps[0].heightInches,
          heightFraction: typeComps[0].heightFraction,
          quantity: typeComps.length,
        });
      }

      return {
        id: `sc-eli-${String(idx + 1).padStart(2, "0")}`,
        estimateId: "sc-est-1",
        structureId: struct.id,
        markNumber: struct.markNumber,
        componentCount,
        totalCubicYards: totalCY,
        totalWeight,
        overallLength: struct.overallLength,
        overallWidth: struct.overallWidth,
        overallHeight: struct.overallHeight,
        stationReference: struct.stationReference,
        componentDefinitions,
        concreteCost,
        laborCost,
        embedCost,
        liftInsertCost,
        waterstopCost,
        subtotal,
        notes: "",
        createdAt: "2025-05-29T00:00:00.000Z",
        updatedAt: "2025-07-08T00:00:00.000Z",
      };
    }
  );

  // Back-fill rolled-up totals on the parent estimate
  backfillEstimateTotals(
    { totalMaterial: runningMaterial, totalLabor: runningLabor },
    estimate
  );

  return items;
};

export const mockStructureCastEstimateLineItems: StructureCastEstimateLineItem[] =
  generateLineItems();

export const createMockStructureCastEstimateLineItemStore = () =>
  new MockServiceStore<StructureCastEstimateLineItem>({
    mockData: mockStructureCastEstimateLineItems,
    idField: "id",
    simulateDelay: 200,
    persistKey: "preferred-structurecast-estimate-line-items",
  });

export default mockStructureCastEstimateLineItems;
